import React, { useCallback } from 'react'

import cn from 'clsx'
import { rem } from 'polished'
import styled from 'styled-components'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'components/Luxkit/Button/TextButton'
import LineArrowRightIcon from 'components/Luxkit/Icons/line/LineArrowRightIcon'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Group from 'components/utils/Group'
import { INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import eases from 'styles/tools/eases'

import OfferDatePicker from '../DatePicker/OfferDatePicker'

const CalendarDatePickerWrapper = styled.div`
  padding-top: ${rem(20)};
`

const FooterContents = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  transform: translateY(100%);
  transition: transform 0.3s ${eases.expo.in};

  &.visible {
    position: unset;
    transform: translateY(0);
    transition: transform 0.3s ${eases.expo.out};
  }
`

interface Props {
  checkIn?: string;
  checkOut?: string;
  onBack: () => void;
  onClose: () => void;
}

function OfferSidebarDatePicker(props: Props) {
  const {
    checkIn,
    checkOut,
    onBack,
    onClose,
  } = props

  const dispatch = useOfferPageDispatch()
  const intlDateFormatter = useIntlDateFormatter()

  const hasDaysSelected = checkIn && checkOut

  const onResetDates = useCallback(() => {
    dispatch({ type: OfferPageActions.resetDates })
  }, [dispatch])

  return (<>
    <ModalHeader
      title="Select your dates"
      onBackButtonClick={onBack}
      onCloseButtonClick={onClose}
    >
    </ModalHeader>
    <ModalBody>
      <ModalContent>
        <CalendarDatePickerWrapper>
          <OfferDatePicker single />
        </CalendarDatePickerWrapper>
      </ModalContent>
    </ModalBody>
    <FooterContents className={cn({ visible: !!hasDaysSelected })}>
      <ModalFooter>
        <VerticalSpacer gap={8}>
          {hasDaysSelected && <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
            <BodyText variant="medium">
              <Group direction="horizontal" verticalAlign="center">
                <span>{intlDateFormatter(checkIn, INTL_SHORT_DAY_NAME_DAY_MONTH)}</span>
                <LineArrowRightIcon size="XS" />
                <span>{intlDateFormatter(checkOut, INTL_SHORT_DAY_NAME_DAY_MONTH)}</span>
              </Group>
            </BodyText>
            <TextButton kind="tertiary" horizontalOutdent="end" onClick={onResetDates}>
              Reset
            </TextButton>
          </Group>}
          <Group direction="horizontal" horizontalAlign="space-between">
            <TextButton kind="primary" size="large" type="submit" fit="mobile-full-width">
              Apply
            </TextButton>
          </Group>
        </VerticalSpacer>
      </ModalFooter>
    </FooterContents>
  </>)
}

export default withOfferPageState((state) => ({
  checkIn: state.checkInDate,
  // Note: we're not using the `getCheckoutDate` selector because we want the explicitly selected
  // check-out date, _not_ the value derived from the check-in date and duration.
  checkOut: state.checkOutDate,
}))(OfferSidebarDatePicker)
